import React, { useEffect, useMemo, useState } from "react"
import { Container, Card, CardBody, Table, Button, Input, Col, Row, Label } from "reactstrap"
import { useNavigate, useSearchParams } from "react-router-dom"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { getExpiryList } from "../../services/Hrms/workforce"
import { toastError } from "../../helpers/msg_helper"

const TYPES = [
  { key: "visa", label: "Visa" },
  { key: "labour_card", label: "MOL / Labour Card" },
  { key: "passport", label: "Passport" },
  { key: "emirates_id", label: "Emirates ID" },
  { key: "iloe", label: "ILOE" },
]
const BUCKETS = [
  { key: "", label: "All (any expiry)" },
  { key: "expired", label: "Expired" },
  { key: "d30", label: "Expiring ≤ 30 days" },
  { key: "d60", label: "Expiring ≤ 60 days" },
]
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

// Defined at module scope (not inside the component) so it isn't remounted on every render —
// remounting mid-interaction was making the dropdowns feel unchangeable.
const Sel = ({ label, value, onChange, children, width }) => (
  <Col xs="auto">
    <Label className="text-muted small mb-1 d-block">{label}</Label>
    <Input type="select" bsSize="sm" value={value} onChange={onChange} style={{ minWidth: width || 130 }}>{children}</Input>
  </Col>
)

const ExpiryList = () => {
  const navigate = useNavigate()
  const [urlParams] = useSearchParams()
  document.title = "Expiry List"

  const thisYear = new Date().getFullYear()
  const years = useMemo(() => Array.from({ length: 11 }, (_, i) => thisYear - 2 + i), [thisYear])

  const [type, setType] = useState(urlParams.get("type") || "visa")
  const [bucket, setBucket] = useState(urlParams.get("bucket") || "")
  const [year, setYear] = useState(urlParams.get("year") || "")
  const [month, setMonth] = useState(urlParams.get("month") || "")
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      try {
        const p = new URLSearchParams({ type })
        if (bucket) p.set("bucket", bucket)
        if (year) p.set("year", year)
        if (month) p.set("month", month)
        const r = await getExpiryList(p.toString())
        setData(r?.data)
      } catch (e) { toastError(e?.message || "Failed to load expiry list") }
      finally { setLoading(false) }
    })()
  }, [type, bucket, year, month])

  const exportCsv = () => {
    const rows = data?.list || []
    if (!rows.length) return
    const head = ["Employee Code", "Name", "Company", `${data.type} No.`, "Expiry Date"]
    const lines = [head.join(",")]
    rows.forEach(r => lines.push([r.code || "", `"${r.employee || ""}"`, `"${r.company || ""}"`, r.number || "", r.expiry_date || ""].join(",")))
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" })
    const a = document.createElement("a")
    a.href = URL.createObjectURL(blob)
    a.download = `${data.type_key}-expiry-list.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  return (
    <div className="page-content report-page">
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <Breadcrumbs title="HRMS" breadcrumbObject={[
            { title: "HRMS", link: "/hrms" },
            { title: "HR Reports", link: "/hrms-dashboard" },
            { title: "Expiry List", link: "#" },
          ]} />
          <Button color="light" size="sm" className="mb-3" onClick={() => navigate("/hrms-expiry-report")}>Pivot Report</Button>
        </div>

        <Card className="mb-3">
          <CardBody>
            <Row className="g-2 align-items-end">
              <Sel label="Document" value={type} onChange={e => setType(e.target.value)} width={170}>
                {TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </Sel>
              <Sel label="Status" value={bucket} onChange={e => setBucket(e.target.value)} width={170}>
                {BUCKETS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
              </Sel>
              <Sel label="Year" value={year} onChange={e => setYear(e.target.value)} width={110}>
                <option value="">All years</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </Sel>
              <Sel label="Month" value={month} onChange={e => setMonth(e.target.value)} width={120}>
                <option value="">All months</option>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </Sel>
              <Col xs="auto" className="ms-auto">
                <Button color="primary" size="sm" onClick={exportCsv} disabled={!data?.list?.length}>Export CSV</Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <h6 className="fw-semibold mb-3">{data?.type || "Expiry"} — {data?.count ?? 0} record(s)</h6>
            <Table responsive borderless className="align-middle tnt-table mb-0">
              <thead><tr><th>Code</th><th>Employee</th><th>Company</th><th>{data?.type} No.</th><th>Expiry Date</th></tr></thead>
              <tbody>
                {(data?.list || []).map((r, i) => (
                  <tr key={i}>
                    <td>{r.code || "-"}</td>
                    <td className="capitalize-letter">{r.employee}</td>
                    <td title={r.company_name}>{r.company || "-"}</td>
                    <td>{r.number || "-"}</td>
                    <td>{r.expiry_date}</td>
                  </tr>
                ))}
                {!loading && !data?.list?.length && <tr><td colSpan="5" className="text-center text-muted">No records for this selection.</td></tr>}
                {loading && <tr><td colSpan="5" className="text-center text-muted">Loading…</td></tr>}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      </Container>
    </div>
  )
}

export default ExpiryList
